import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { visaApi, VISA_STATUS_BY_ID } from "@/lib/visa";
import { relativeTime, countryFlag } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Plus, Search, Globe2 } from "lucide-react";
import { toast } from "sonner";
import { getApiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const roleBase = (role) =>
  ({ admin: "/admin", coordinator: "/coordinator", partner: "/partner", patient: "/portal" }[role] || "/admin");

export default function VisaRequests() {
  const { user } = useAuth();
  const base = roleBase(user?.role);
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [country, setCountry] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await visaApi.listRequests({
        q: q || undefined,
        status: status || undefined,
        country: country || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      });
      setItems(data);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [q, status, country, dateFrom, dateTo]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="visa-requests-page">
      <PageHeader
        eyebrow="International Services"
        title="Visa Invitation Requests"
        subtitle="End-to-end management of Medical Visa Invitation Letters for international patients."
        actions={
          <button
            onClick={() => navigate(`${base}/visa/new`)}
            className="bg-[#2A4D3E] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:opacity-90 flex items-center gap-2"
            data-testid="new-visa-request-btn"
          >
            <Plus size={16} /> New Request
          </button>
        }
      />

      {/* Filters */}
      <div className="bg-white border border-[#E8E6E1] rounded-2xl p-4 mb-5 grid grid-cols-1 md:grid-cols-6 gap-3" data-testid="visa-filters">
        <div className="md:col-span-2 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E9992]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search name, passport, reference…"
            className="w-full border border-[#E8E6E1] rounded-lg pl-9 pr-3 py-2 text-sm focus:border-[#2A4D3E] outline-none"
            data-testid="visa-search-input"
          />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-[#E8E6E1] rounded-lg px-3 py-2 text-sm" data-testid="visa-status-filter">
          <option value="">All statuses</option>
          {Object.values(VISA_STATUS_BY_ID).map((s) => (<option key={s.id} value={s.id}>{s.label}</option>))}
        </select>
        <input value={country} onChange={(e) => setCountry(e.target.value)} placeholder="Nationality" className="border border-[#E8E6E1] rounded-lg px-3 py-2 text-sm" data-testid="visa-country-filter" />
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="border border-[#E8E6E1] rounded-lg px-3 py-2 text-sm" data-testid="visa-datefrom-filter" />
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="border border-[#E8E6E1] rounded-lg px-3 py-2 text-sm" data-testid="visa-dateto-filter" />
      </div>

      {loading ? (
        <div className="text-sm text-[#5C6660]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-[#E8E6E1] rounded-2xl p-10 text-center">
          <Globe2 size={28} className="mx-auto text-[#8E9992] mb-2" />
          <div className="text-sm text-[#5C6660] mb-4">No visa requests yet.</div>
          <button onClick={() => navigate(`${base}/visa/new`)} className="bg-[#2A4D3E] text-white rounded-full px-5 py-2 text-sm font-medium">Create first request</button>
        </div>
      ) : (
        <div className="bg-white border border-[#E8E6E1] rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAF7] border-b border-[#E8E6E1]">
              <tr>
                <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider text-[#8E9992] font-semibold">Reference</th>
                <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider text-[#8E9992] font-semibold">Patient</th>
                <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider text-[#8E9992] font-semibold">Nationality</th>
                <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider text-[#8E9992] font-semibold">Hospital</th>
                <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider text-[#8E9992] font-semibold">Status</th>
                <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider text-[#8E9992] font-semibold">Created</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const st = VISA_STATUS_BY_ID[r.status] || VISA_STATUS_BY_ID.draft;
                const p = r.patient_info || {};
                const m = r.medical_info || {};
                return (
                  <tr key={r.id} onClick={() => navigate(`${base}/visa/${r.id}`)}
                    className="border-b border-[#F2F4F3] hover:bg-[#FAFAF7] cursor-pointer" data-testid={`visa-row-${r.id}`}>
                    <td className="px-4 py-3 font-mono text-xs text-[#2A4D3E]">{r.reference_number || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#2A332C]">{p.full_name || "—"}</div>
                      <div className="text-[11px] text-[#8E9992]">{p.passport_number || "no passport"}</div>
                    </td>
                    <td className="px-4 py-3 text-[#5C6660]">{countryFlag(p.nationality)} {p.nationality || "—"}</td>
                    <td className="px-4 py-3 text-[#5C6660]">{m.hospital_name || "—"}</td>
                    <td className="px-4 py-3">
                      <span style={{ background: st.bg, color: st.color }} className="px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider">{st.label}</span>
                    </td>
                    <td className="px-4 py-3 text-[#8E9992] text-xs">{relativeTime(r.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
